import { ArrowPathIcon } from "@heroicons/react/24/outline";
import React from "react";
import { runDroppedRate } from "../../utils/matchRate";
import { ScheduledJobRun, UnmatchedUrl } from "./types";

// bd-deepcontext-hfv7u.15 — per-run detail panel, trimmed. The chart tooltip now
// carries the per-run stats (requested / processed / matched / unmatched / match
// rate / duration / avg confidence), so the old summary block (status pill, big
// match-rate %, matched/unmatched/processed/duration grid, avg confidence) is
// redundant and removed. What remains anchors the area: a single minimal
// '{time} · {status}' label, plus the non-redundant per-run controls/states that
// the chart does NOT show — Cancel (running/pending), a progress bar + chunk
// count (running), an error box (failed), a note box (partial/cancelled/timed-
// out), and the Top-unmatched-URLs drill-down (with the Borderline/Clear-
// rejections sort). Pure presentation: all data, the drill-down state, and the
// cancel/drill-down handlers are owned by the modal and threaded through props.
export interface JobRunDetailProps {
  run: ScheduledJobRun;
  jobId: string;
  // Unmatched-URL drill-down (per run) state, owned by the modal.
  expandedRunId: string | null;
  unmatchedSort: "lowest" | "highest";
  unmatchedUrls: UnmatchedUrl[];
  unmatchedLoading: boolean;
  unmatchedError: string | null;
  // Handlers, threaded from the modal (which wires them to the API).
  onCancelRun: (jobId: string, runId: string) => void;
  onToggleDrilldown: (jobId: string, runId: string) => void;
  onChangeUnmatchedSort: (
    jobId: string,
    runId: string,
    sort: "lowest" | "highest",
  ) => void;
  // Formatting helpers, shared with the parent so output stays consistent.
  formatDate: (dateString: string, showTimezone?: boolean) => string;
  calculateDuration: (startedAt?: string, completedAt?: string) => string;
  getRunStatusColor: (status: string) => string;
  getRunStatusIcon: (status: string) => React.ReactNode;
}

// Status label for the minimal run-label line — '{time} · {Status}'. Title-cased
// so the line reads "… · Completed" / "… · Failed" / "… · Running" / "… · Pending".
const STATUS_LABEL: Record<ScheduledJobRun["status"], string> = {
  completed: "Completed",
  failed: "Failed",
  running: "Running",
  pending: "Pending",
};

const JobRunDetail: React.FC<JobRunDetailProps> = ({
  run,
  jobId,
  expandedRunId,
  unmatchedSort,
  unmatchedUrls,
  unmatchedLoading,
  unmatchedError,
  onCancelRun,
  onToggleDrilldown,
  onChangeUnmatchedSort,
  formatDate,
  calculateDuration,
  // getRunStatusColor / getRunStatusIcon are still accepted (the modal threads
  // them through) but no longer used — the summary block they styled is gone.
}) => {
  const summary = run.resultsSummary;
  const canCancel = run.status === "running" || run.status === "pending";
  // bd-deepcontext-qx0pm — dropped/not-classified count (bd-wte47). URLs
  // submitted to the pipeline that never returned a classification (chunk
  // timeouts / extraction failures). Surfaced as a badge + note box ONLY when a
  // run actually dropped URLs, so zero-drop runs render exactly as before.
  const droppedUrls = summary?.droppedUrls ?? 0;
  const hasDropped = droppedUrls > 0;
  const droppedPct = (runDroppedRate(run) * 100).toFixed(1);
  // bd-deepcontext-8dzk5 — heavy-drop escalation badge. Reads the same
  // results_summary JSON the dropped band already reads; `degraded` is set by
  // the backend when a completed run lost >= 10% of its submitted URLs (status
  // stays "completed"). Rendered only when the flag is present + true, so runs
  // predating the feature look exactly as before.
  const isDegraded = summary?.degraded === true;

  return (
    <div className="border rounded-lg p-4">
      {/* Minimal run-label line — '{time} · {Status}' — anchors the area now that
          the summary block (stats + match-rate %) lives in the chart tooltip. */}
      <div className="flex items-start justify-between gap-2">
        <span className="inline-flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
          <span>
            {formatDate(run.createdAt)} · {STATUS_LABEL[run.status]}
          </span>
          {/* bd-deepcontext-qx0pm — amber dropped badge, only when this run
              dropped URLs. */}
          {hasDropped && (
            <span className="inline-flex items-center rounded-full bg-warn-bg px-2 py-0.5 text-xs font-medium text-warn">
              {droppedUrls} dropped
            </span>
          )}
          {/* bd-deepcontext-8dzk5 — rose "Degraded" badge escalating a completed
              run that dropped >= 10% of submitted URLs (status stays completed). */}
          {isDegraded && (
            <span
              className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700"
              title={`Degraded run — ${droppedPct}% of submitted URLs were dropped (>= 10% threshold)`}
            >
              Degraded
            </span>
          )}
        </span>
        {canCancel && (
          <button
            onClick={() => onCancelRun(jobId, run.id)}
            className="px-2 py-1 bg-risk-bg text-risk hover:opacity-70 rounded-md transition-colors inline-flex items-center gap-1 text-xs font-medium"
            title="Cancel this job run"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-4 w-4 flex-shrink-0"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z"
              />
            </svg>
            <span>Cancel</span>
          </button>
        )}
      </div>

      {/* Progress bar + chunks for running jobs. */}
      {run.status === "running" && summary?.progress !== undefined && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-ink-muted">
              Progress: {summary.chunksCompleted || 0} /{" "}
              {summary.totalChunks || 0} chunks
            </span>
            <span className="font-medium">{summary.progress}%</span>
          </div>
          <div className="w-full bg-ghost-bg rounded-full h-2.5">
            <div
              className="bg-accent h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${summary.progress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* bd-deepcontext-qx0pm — amber note box for dropped/not-classified URLs,
          reusing the note-box pattern. Shown only when droppedUrls > 0. */}
      {hasDropped && (
        <div className="mt-2 p-2 bg-warn-bg border border-warn rounded text-sm text-warn">
          <span className="font-medium">Dropped (not classified):</span>{" "}
          {droppedUrls} of {summary?.totalUrls ?? 0} submitted URLs ({droppedPct}%)
          never returned a classification — chunk timeouts or extraction
          failures. They are excluded from matched and unmatched counts.
        </div>
      )}

      {/* Note box for partial / cancelled / timed-out results. */}
      {summary?.note && (
        <div className="mt-2 p-2 bg-warn-bg border border-warn rounded text-sm text-warn">
          <span className="font-medium">Note:</span> {summary.note}
        </div>
      )}

      {/* Error box for failed runs. */}
      {run.errorMessage && (
        <div className="mt-2 p-2 bg-risk-bg border border-risk rounded text-sm text-risk">
          <span className="font-medium">Error:</span> {run.errorMessage}
        </div>
      )}

      {/* Unmatched-URL drill-down trigger + panel — reused unchanged.
          Only meaningful once a run has produced results. */}
      {(run.status === "completed" || run.status === "failed") && (
        <div className="mt-3 border-t border-line pt-3">
          <button
            type="button"
            onClick={() => onToggleDrilldown(jobId, run.id)}
            className="text-sm font-medium text-accent hover:text-accent-active inline-flex items-center gap-1"
            aria-expanded={expandedRunId === run.id}
          >
            {expandedRunId === run.id ? "▼" : "▶"} Top unmatched URLs
          </button>

          {expandedRunId === run.id && (
            <div className="mt-3 bg-ghost-bg border border-line rounded p-3">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-ink-muted mr-1">Order by:</span>
                <button
                  type="button"
                  onClick={() =>
                    onChangeUnmatchedSort(jobId, run.id, "lowest")
                  }
                  className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                    unmatchedSort === "lowest"
                      ? "bg-accent text-white border-accent"
                      : "bg-surface text-ink border-line hover:bg-ghost-bg"
                  }`}
                  title="Lowest confidence first — close-call rejections, useful for blueprint tuning"
                >
                  Borderline
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onChangeUnmatchedSort(jobId, run.id, "highest")
                  }
                  className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                    unmatchedSort === "highest"
                      ? "bg-accent text-white border-accent"
                      : "bg-surface text-ink border-line hover:bg-ghost-bg"
                  }`}
                  title="Highest confidence first — clear rejections, useful for scan-pool quality audits"
                >
                  Clear rejections
                </button>
              </div>

              {unmatchedLoading ? (
                <div className="text-sm text-ink-muted py-2 inline-flex items-center gap-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Loading unmatched URLs…
                </div>
              ) : unmatchedError ? (
                <div className="text-sm text-risk">{unmatchedError}</div>
              ) : unmatchedUrls.length === 0 ? (
                <div className="text-sm text-ink-muted italic">
                  No unmatched URLs for this run.
                </div>
              ) : (
                <ul className="space-y-2">
                  {unmatchedUrls.map((u, i) => (
                    <li
                      key={`${run.id}-unmatched-${i}`}
                      className="bg-surface border border-line rounded p-2 text-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <a
                          href={u.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-active hover:underline break-all font-mono text-xs"
                        >
                          {u.url}
                        </a>
                        <span className="text-xs font-medium text-ink whitespace-nowrap">
                          conf:{" "}
                          {Number.isFinite(u.confidence)
                            ? u.confidence.toFixed(2)
                            : "—"}
                        </span>
                      </div>
                      {u.reasoning && (
                        <div className="mt-1 text-xs text-ink-muted whitespace-pre-wrap">
                          {u.reasoning}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobRunDetail;
